import React, {useCallback, useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {DrawerContentType} from '../WorkspaceDrawerContextProvider';
import {BackButton} from './components/BackButton';
import {useWorkspaceSettingOverrides} from './hooks/useSettingOverrides';
import {WorkspaceMediaSettingsDrawer} from './media/WorkspaceMediaSettingsDrawer';
import {LinePlotSettings} from './runLinePlots/LinePlotSettings';
import {LinePlotSettingsContextProvider} from './runLinePlots/LinePlotSettingsContextProvider';
import {SemanticLegendSettings} from './semanticLegend/SemanticLegendSettings';
import {SelectedSettingType} from './types';
import {WorkspaceLayoutSettings} from './workspaceLayout/WorkspaceLayoutSettings';
import {useWorkspaceSettingsContext} from './WorkspaceSettingsContextProvider';
import {SettingsMenu} from './WorkspaceSettingsMenu';

export const WorkspaceSettingsContent = ({
  displayedContentType,
}: {
  displayedContentType: DrawerContentType;
}) => {
  return (
    <>
      <SettingComponent displayedContentType={displayedContentType} />
      {/* TODO - Add back in when we work on preview mode */}
      {/* <div
        className={twMerge(
          'flex h-[80px] w-[inherit]',
          'fixed bottom-0',
          'gap-24 px-24 py-20',
          'border-t-[1px] border-solid border-moon-250',
          '[&>*]:grow'
        )}>
        <Button
          variant="secondary"
          className="grow"
          onClick={closeWorkspaceDrawer}>
          Cancel
        </Button>
        <Button variant="primary" className="grow" disabled>
          Apply
        </Button>
      </div> */}
    </>
  );
};

const SettingComponent = ({
  displayedContentType,
}: {
  displayedContentType: DrawerContentType;
}) => {
  const {
    linePlot,
    trackWorkspaceSettingChange,
    updateLinePlotWorkspaceSettings,
  } = useWorkspaceSettingsContext();
  const {clearSetting, settingOverrideCounts} = useWorkspaceSettingOverrides();

  const [selectedSettingType, setSelectedSettingType] = useState<
    SelectedSettingType | undefined
  >();
  const setSelectedSettingTypeAndLog = useCallback(
    (settingType: SelectedSettingType | undefined) => {
      setSelectedSettingType(settingType);
      Analytics.track('Select Workspace Setting Menu Item', {
        settingType,
      });
    },
    [setSelectedSettingType]
  );

  if (selectedSettingType == null) {
    return (
      <SettingsMenu setSelectedSettingType={setSelectedSettingTypeAndLog} />
    );
  }

  if (selectedSettingType === 'workspace-layout') {
    return (
      <WorkspaceLayoutSettings
        setSelectedSettingType={setSelectedSettingTypeAndLog}
      />
    );
  }

  const backButton = (
    <BackButton onClick={() => setSelectedSettingTypeAndLog(undefined)} />
  );

  if (selectedSettingType === 'line-plot') {
    return (
      <LinePlotSettingsContextProvider
        clearSetting={clearSetting}
        displayLinePlotSettings={linePlot}
        settingLevel="workspace"
        settingOverrideCounts={settingOverrideCounts}
        trackLinePlotSetting={trackWorkspaceSettingChange}
        updateLinePlotSettings={updateLinePlotWorkspaceSettings}>
        <LinePlotSettings
          contentHeader={backButton}
          displayedContentType={displayedContentType}
        />
      </LinePlotSettingsContextProvider>
    );
  }

  if (selectedSettingType === 'semantic-legend') {
    return (
      <SemanticLegendSettings
        contentHeader={backButton}
        displayedContentType={displayedContentType}
      />
    );
  }

  if (selectedSettingType === 'media') {
    return <WorkspaceMediaSettingsDrawer header={backButton} />;
  }

  return null;
};
